import { TABS, TAB_FIELDS, isFieldRequired } from '../../config/tabs.js';
import { POLICY_TEXT } from '../../config/settings.js';
import { state, set, patch, subscribe } from '../../state/store.js';
import { escapeHtml } from '../../lib/format.js';
import { DEFAULT_COUNTRY } from '../../lib/countries.js';
import { mountCountrySelect } from './countrySelect.js';

const $tabs = () => document.getElementById('tabs-root');
const $fields = () => document.getElementById('contact-fields');
const $policy = () => document.getElementById('policy-list');

const countryKey = (fieldName) => `${fieldName}Country`;

function renderTabs() {
  const root = $tabs();
  if (!root) return;
  root.innerHTML = TABS.map((t) =>
    `<button type="button" class="tab${state.tab === t.id ? ' is-active' : ''}" role="tab" data-tab="${t.id}">${escapeHtml(t.label)}</button>`
  ).join('');
  root.querySelectorAll('.tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      set({ tab: btn.dataset.tab });
    });
  });
}

function renderFields() {
  const root = $fields();
  if (!root) return;
  const fields = TAB_FIELDS[state.tab] || [];
  const errors = state.ui?.errors || {};
  const methodId = state.delivery?.methodId;

  const html = fields.map((f) => {
    const isPhone = f.type === 'phone';
    const required = isFieldRequired(f, methodId);
    const className = `field${f.full || isPhone ? ' full' : ''}${errors[f.name] ? ' has-error' : ''}`;
    const placeholder = `${f.label}${required ? ' *' : ''}`;
    const value = escapeHtml(state.contact?.[f.name] ?? '');
    const errMsg = errors[f.name]
      ? `<div class="field-error">${escapeHtml(errors[f.name])}</div>`
      : '';
    if (isPhone) {
      return `
        <div class="${className}" data-name="${f.name}" data-required="${required ? '1' : '0'}">
          <div class="phone-field">
            <button type="button" class="phone-prefix" data-country-trigger="${f.name}"></button>
            <input class="input" type="tel" name="${f.name}" placeholder="${escapeHtml(placeholder)}" value="${value}" />
          </div>
          ${errMsg}
        </div>`;
    }
    return `
      <div class="${className}" data-name="${f.name}" data-required="${required ? '1' : '0'}">
        <input class="input" type="${f.type}" name="${f.name}" placeholder="${escapeHtml(placeholder)}" value="${value}" />
        ${errMsg}
      </div>`;
  }).join('');

  root.innerHTML = `<div class="field-grid">${html}</div>`;

  // Wire text inputs. For Ukrainian phone numbers we normalise the input to
  // the 9-digit local form (no spaces, no +380, no leading 0). The country
  // dial code is shown by the phone-prefix button next to the input, so the
  // customer's actual typed digits are always the local part.
  root.querySelectorAll('input').forEach((input) => {
    input.addEventListener('input', () => {
      let value = input.value;
      if (input.type === 'tel') {
        const country = (state.contact?.[countryKey(input.name)] || DEFAULT_COUNTRY).toUpperCase();
        if (country === 'UA') {
          // Keep digits only, drop a pasted "+380" / "380" prefix, drop
          // the trunk-0 ("0682…" → "682…"), cap at 9 digits.
          let digits = value.replace(/\D/g, '');
          if (digits.startsWith('380')) digits = digits.slice(3);
          digits = digits.replace(/^0+/, '').slice(0, 9);
          if (digits !== value) {
            input.value = digits;
            try { input.setSelectionRange(digits.length, digits.length); } catch (_) {}
            value = digits;
          }
        }
      }
      patch('contact', { [input.name]: value });
    });
  });

  // Mount country picker on each phone field.
  root.querySelectorAll('[data-country-trigger]').forEach((trigger) => {
    const fieldName = trigger.dataset.countryTrigger;
    const initial = state.contact?.[countryKey(fieldName)] || DEFAULT_COUNTRY;
    mountCountrySelect(trigger, {
      value: initial,
      onChange: (code) => {
        patch('contact', { [countryKey(fieldName)]: code });
      },
    });
  });
}

function renderPolicy() {
  const ul = $policy();
  if (!ul) return;
  ul.innerHTML = POLICY_TEXT.map((t) => `<li>${escapeHtml(t)}</li>`).join('');
}

export function initContact() {
  renderTabs();
  renderFields();
  renderPolicy();

  subscribe(['tab'], () => {
    renderTabs();
    renderFields();
  });
  subscribe(['ui'], () => {
    renderFields();
  });

  // Re-render when the delivery method changes so the patronymic field's
  // asterisk flips on/off for Укрпошта. Gated to method changes only — we
  // don't want to rebuild the form on every keystroke inside delivery.
  let lastMethodId = state.delivery?.methodId;
  subscribe(['delivery'], () => {
    if (state.delivery?.methodId !== lastMethodId) {
      lastMethodId = state.delivery?.methodId;
      renderFields();
    }
  });
}
